import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, Divider, Typography } from "@mui/material";

import APP_ROUTES from "../../../../../../routers/routeConstants";

import { useServiceProfile } from "../../../../../GoService/api/queries/useServiceProfile";
import { StatusCard, StatusCardLoader } from "../../../../../GoService/components/StatusCard";

import { GoServiceTitleIcon } from "../../../../assets/icons/go-service-icons";
import { NoServicesProvidedWarningIcon } from "../../../../assets/icons/entity-icons";

import ServiceOverview from "./ServiceOverview";

const ServiceProvided = ({
  entityUserId,
  isOwn,
  isDataProfileDataLoading,
  IS_NO_SERVICES_PROVIDED,
}: {
  entityUserId?: string;
  isOwn?: boolean;
  isDataProfileDataLoading?: boolean;
  IS_NO_SERVICES_PROVIDED?: boolean;
}) => {
  const { t } = useTranslation("profiles", {
    keyPrefix: "goService",
  });

  const navigate = useNavigate();
  const { userId } = useParams();
  const ID = entityUserId || userId;

  const { data: serviceProfile, isLoading: isServiceProfileLoading } = useServiceProfile(Number(ID));

  const { overview, statuses } = serviceProfile || {};

  const IS_LOADING = isServiceProfileLoading || Boolean(isDataProfileDataLoading);

  const handleNavigateToServicesRequests = () => navigate(APP_ROUTES.MY_SERVICES);

  const handleNavigateToCreateService = () => navigate(APP_ROUTES.CREATE_SERVICE);

  return (
    <Box sx={styles.serviceProvidedContainer} component="section">
      <Typography sx={styles.serviceProvidedTitle}>
        <GoServiceTitleIcon />
        {t("serviceProvided")}
      </Typography>
      <Divider sx={styles.divider} orientation="horizontal" />
      <ServiceOverview serviceOverview={overview} isLoading={IS_LOADING} isOwn={isOwn} userId={ID} />
      <Box sx={styles.statusesContainer}>
        {IS_LOADING && [...Array(3)].map((_, index) => <StatusCardLoader key={index} />)}
        {!IS_LOADING && statuses?.map((status) => <StatusCard key={status.title} status={status} />)}
      </Box>
      {isOwn && (
        <>
          {IS_NO_SERVICES_PROVIDED && <NoServicesProvidedWarning />}
          <Divider sx={styles.divider} orientation="horizontal" />
          <Box sx={styles.buttonsContainer}>
            <Button variant="outlined-md" onClick={handleNavigateToServicesRequests}>
              {t("servicesRequests")}
            </Button>

            <Button variant="primary-md" onClick={handleNavigateToCreateService}>
              {t("createService")}
            </Button>
          </Box>
        </>
      )}

      {/* <ServiceCategory categories={categories} isLoading={isServiceProfileLoading} /> */}
    </Box>
  );
};

export default ServiceProvided;

const NoServicesProvidedWarning = () => {
  const { t } = useTranslation("profiles", {
    keyPrefix: "goService",
  });

  return (
    <Box sx={styles.noServicesProvidedWarningContainer}>
      <NoServicesProvidedWarningIcon />
      <Typography sx={styles.noServicesProvidedWarning}>{t("noServicesProvided")}</Typography>
    </Box>
  );
};

const styles = {
  serviceProvidedContainer: {
    padding: "20px 24px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  noServicesProvidedWarningContainer: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    borderRadius: "8px",
    border: "1px dashed var(--Warning-Warning, #F6C000)",
    background: "var(--Warning-Warning-Light, #FFF8DD)",
    padding: "12px 14px",
  },
  noServicesProvidedWarning: {
    fontFamily: "Montserrat",
    color: "var(--Dark---800, #414141)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  serviceProvidedTitle: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  divider: {
    borderColor: "#EAECF0",
    margin: "0 -24px",
  },
  statusesContainer: {
    display: "grid",
    gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(3, 1fr)" },
    gap: "16px",
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: "12px",
    justifyContent: { sm: "flex-end" },
  },
};
